import type {
  CreateShipmentReqBody,
  UpdateShipmentReqBody,
} from '../../../../apis/dealsAPI'
import type { ShipmentLegDto } from '../../../../apis/hey-api/types.gen'
import type { Shipment, ShipmentLeg } from '../../types/deal'

// Shipments newly added in the modal carry a synthetic id with this prefix
// until the server-side create returns a real ObjectId. See diffShipments
// below for how the prefix routes them into `toCreate`.
export const TEMP_SHIPMENT_PREFIX = 'tmp_'

export interface ShipmentDiff {
  toCreate: Shipment[]
  toUpdate: Shipment[]
  toDelete: Shipment[]
}

function isTempId(id: string): boolean {
  return id.startsWith(TEMP_SHIPMENT_PREFIX)
}

function shipmentsEqual(a: Shipment, b: Shipment): boolean {
  return (
    a.label === b.label &&
    a.carrier === b.carrier &&
    a.trackingNumber === b.trackingNumber &&
    a.linkedInvoiceNumber === b.linkedInvoiceNumber &&
    JSON.stringify(a.devices) === JSON.stringify(b.devices) &&
    JSON.stringify(a.legs) === JSON.stringify(b.legs)
  )
}

export function diffShipments(original: Shipment[], modified: Shipment[]): ShipmentDiff {
  const originalById = new Map(original.map((shipment) => [shipment.id, shipment]))
  const modifiedById = new Map(modified.map((shipment) => [shipment.id, shipment]))

  const toCreate: Shipment[] = []
  const toUpdate: Shipment[] = []
  const toDelete: Shipment[] = []

  modified.forEach((shipment) => {
    if (isTempId(shipment.id)) {
      toCreate.push(shipment)
      return
    }
    const previous = originalById.get(shipment.id)
    if (previous && !shipmentsEqual(previous, shipment)) {
      toUpdate.push(shipment)
    }
  })

  original.forEach((shipment) => {
    if (!modifiedById.has(shipment.id)) toDelete.push(shipment)
  })

  return { toCreate, toUpdate, toDelete }
}

function legToDto(leg: ShipmentLeg): ShipmentLegDto {
  // Response leg allows statusDate: null; request DTO only accepts
  // string | undefined, so collapse the null sentinel.
  return {
    type: leg.type,
    status: leg.status,
    carrier: leg.carrier,
    trackingNumber: leg.trackingNumber,
    statusDate: leg.statusDate ?? undefined,
  }
}

export function shipmentToCreateBody(shipment: Shipment): CreateShipmentReqBody {
  return {
    label: shipment.label,
    linkedInvoiceNumber: shipment.linkedInvoiceNumber,
    carrier: shipment.carrier,
    trackingNumber: shipment.trackingNumber,
    devices: shipment.devices,
    legs: shipment.legs.map(legToDto),
  }
}

export function shipmentToUpdateBody(shipment: Shipment): UpdateShipmentReqBody {
  return {
    label: shipment.label,
    linkedInvoiceNumber: shipment.linkedInvoiceNumber,
    carrier: shipment.carrier,
    trackingNumber: shipment.trackingNumber,
    devices: shipment.devices,
    legs: shipment.legs.map(legToDto),
  }
}
